import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, NgForm, ReactiveFormsModule, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { GlobalFunctions } from '../../../core/global-functions';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '../../../core/auth/auth.service';

import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { CONSTANTS } from '../../../core/constants';
import { LocalStorageHelper } from '../../../core/helpers/local-storage.helper';


@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, NgxIntlTelInputModule, ReactiveFormsModule, RouterModule, ToastModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  providers: [GlobalFunctions]
})
export class RegisterComponent implements OnInit {

  @Input() defaultTab: 'student' | 'tutor' = 'student';
  @Input() hideTabs: boolean = false;

  registerForm: any = FormGroup;
  isSelectedStudent: boolean = true;
  selectedUsertype: string = '';

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.India];
  phoneForm: any = new FormGroup({
    phone: new FormControl(null, [Validators.required]),
  });
  isPhoneFormTrue: boolean = false;
  isBtnLoading: boolean = false;
  submitted: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  @ViewChild('registerNgForm') registerNgForm!: any;
  constants: any = CONSTANTS;

  constructor(
    private _globalFunctions: GlobalFunctions,
    private _formBuilder: FormBuilder,
    private messageService: MessageService,
    private _authServices: AuthService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private localStorage: LocalStorageHelper
  ) {
    this.selectedUsertype = this.constants?.usertypeObj?.student;
  }

  ngOnInit(): void {
    this.prepareRegisterFormObj();

    // Apply defaultTab for embedded usage
    if (this.defaultTab === 'tutor') {
      this.setUserType(this.constants?.usertypeObj?.tutor);
    } else {
      this.setUserType(this.constants?.usertypeObj?.student);
    }

    // Check for tab query parameter to pre-select tutor tab
    this._activatedRoute.queryParams.subscribe(params => {
      if (params['tab'] === 'tutor') {
        this.setUserType(this.constants?.usertypeObj?.tutor);
      }
      if (params['tab'] === 'student') {
        this.setUserType(this.constants?.usertypeObj?.student);
      }
    });

    if (typeof window !== 'undefined' && this.localStorage.getItem('userEmail')) {
      this.localStorage.removeItem('userEmail');
    }
  }

  onSubmit() {
    if (this.registerForm.valid) {
      const { role, ...rest } = this.registerForm.value;
      if (role === 'student') {
        this._authServices.studentRegister(rest).subscribe(
          (response) => {
            this.localStorage.setItem('userEmail', this.registerForm?.value?.email);
            if (this.localStorage.getItem('userEmail')) {
              this._router.navigate(['/verify-otp']);
            }
          },
          (error: any) => {
            console.error('Student registration failed', error);
          }
        );
      } else {
        this._authServices.tutorRegister(rest).subscribe(
          (response) => {
            this.localStorage.setItem('userEmail', this.registerForm?.value?.email);
            if (this.localStorage.getItem('userEmail')) {
              this._router.navigate(['/verify-otp']);
            }
          },
          (error: any) => {
            console.error('Tutor registration failed', error);
          }
        );
      }
    }
  }

  selectUserType(type: any) {
    this.setUserType(type);
  }

  private setUserType(type: any): void {
    const isTutor = type === this.constants?.usertypeObj?.tutor;
    this.isSelectedStudent = !isTutor;
    this.selectedUsertype = isTutor ? this.constants?.usertypeObj?.tutor : this.constants?.usertypeObj?.student;
    this.isPhoneFormTrue = isTutor;

    // Keep a hidden role value in the form so other code paths don't crash.
    if (this.registerForm?.get('role')) {
      this.registerForm.get('role')?.setValue(this.selectedUsertype);
    }
  }

  registerNow() {
    this.submitted = true;
    this.isBtnLoading = true;
    if (this.selectedUsertype == this.constants?.usertypeObj?.student) {
      this.registerAsStudent();
    } else {
      this.registerAsTutor();
    }
  }

  registerAsStudent() {
    if (this.registerForm.invalid) {
      Object.keys(this.registerForm.controls).forEach((key: any) => {
        this.registerForm.controls[key].touched = true;
        this.registerForm.controls[key].markAsDirty();
      });
      this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please enter valid data' });
      this.isBtnLoading = false;
      return;
    };
    let userObj: any = {
      fname: this.registerForm?.value?.fname,
      lname: this.registerForm?.value?.lname,
      email: this.registerForm?.value?.email,
      password: this.registerForm?.value?.password,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    this.registerForm.disable();
    this.phoneForm.disable();
    this._authServices.studentRegister(userObj).subscribe({
      next: (result: any) => {
        if (result && result?.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result?.message || result?.Message || 'OTP sent successfully!' });
          if (typeof window != 'undefined') {
            this.localStorage.setItem('userEmail', this.registerForm?.value?.email);
            if (this.localStorage.getItem('userEmail')) {
              this._router.navigate(['/verify-otp']);
            }
          }
          this.isBtnLoading = false;
        } else {
          this.isBtnLoading = false;
          this.registerForm.enable();
          this.phoneForm.enable();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: result?.message || result?.Message || 'Registration failed' });
        }
      },
      error: (error: any) => {
        this.isBtnLoading = false;
        this.registerForm.enable();
        this.phoneForm.enable();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error?.message || error.error?.Message || error.message || 'Registration failed' });
      }
    });
  }

  registerAsTutor() {
    if (this.registerForm.invalid) {
      Object.keys(this.registerForm.controls).forEach((key: any) => {
        this.registerForm.controls[key].touched = true;
        this.registerForm.controls[key].markAsDirty();
      });
      this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please enter valid data' });
      this.isBtnLoading = false;
      return;
    };
    if (this.phoneForm.invalid) {
      Object.keys(this.phoneForm.controls).forEach((key) => {
        this.phoneForm.controls[key].touched = true;
        this.phoneForm.controls[key].markAsDirty();
      });
      this.isBtnLoading = false;
      this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please enter valid phone data' });
      return;
    }
    let userObj: any = {
      fname: this.registerForm?.value?.fname,
      lname: this.registerForm?.value?.lname,
      email: this.registerForm?.value?.email,
      password: this.registerForm?.value?.password,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    userObj.country_wise_contact = this.phoneForm?.controls?.phone?.value || "";
    const contactNumber = userObj?.country_wise_contact?.e164Number;
    userObj.country_code = userObj?.country_wise_contact?.dialCode || "";
    userObj.mobile = contactNumber.replace(userObj?.country_code, '') || '';

    this.registerForm.disable();
    this.phoneForm.disable();

    this._authServices.tutorRegister(userObj).subscribe({
      next: (result: any) => {
        if (result && result?.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result?.message || result?.Message || 'OTP sent successfully!' });
          if (typeof window != 'undefined') {
            this.localStorage.setItem('userEmail', this.registerForm?.value?.email);
            if (this.localStorage.getItem('userEmail')) {
              this._router.navigate(['/verify-otp']);
            }
          }
          this.isBtnLoading = false;
          this.registerForm.enable();
          this.phoneForm.enable();
        } else {
          this.isBtnLoading = false;
          this.registerForm.enable();
          this.phoneForm.enable();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: result?.message || result?.Message || 'Registration failed' });
        }
      },
      error: (error: any) => {
        this.isBtnLoading = false;
        this.registerForm.enable();
        this.phoneForm.enable();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error?.message || error.error?.Message || error.message || 'Registration failed' });
      }
    });
  }

  passwordMatchValidator: ValidatorFn = (control: AbstractControl): { [key: string]: boolean } | null => {
    const password = control.get('password');
    const confirm_password = control.get('confirm_password');
    if (password && confirm_password && password.value !== confirm_password.value) {
      return { 'passwordMismatch': true };
    }
    return null;
  };

  prepareRegisterFormObj() {
    this.registerForm = this._formBuilder.group({
      fname: ['', [Validators.required]],
      lname: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      confirm_password: ['', Validators.required],
    }, { validators: this.passwordMatchValidator })
  }

}